import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// AlarmManager.jsx - WEBSOCKET VERSION: Sistema de tickets en tiempo real
// CAMBIOS:
// - ELIMINADO: useTickets hook con polling HTTP
// - AÑADIDO: useTicketSubscription para WebSocket en tiempo real
// - AÑADIDO: useAlarmSubscription para alarmas en tiempo real

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box,
    Alert,
    Collapse,
    IconButton,
    Typography,
    Chip,
    Snackbar,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    VolumeOff as MuteIcon,
    VolumeUp as UnmuteIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Email as EmailIcon,
    Notifications as NotificationIcon,
    CheckCircle as CheckIcon,
    Assignment as TicketIcon
} from '@mui/icons-material';

// 🔄 WEBSOCKET: Importar hooks de WebSocket en lugar de useTickets
import { useTicketSubscription, useAlarmSubscription } from '../../hooks/useWebSocket';
import { useNotification } from '../common/GlobalNotifications';
import { syncAlarmSound, testAlarmSound as playTestSound } from '../../services/alarmSoundService';
import { useAlarmMute } from '../../hooks/useAlarmMute';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';

const AlarmManager = ({
    realtimeData = {},
    vehicles = [],
    onAlarmStateChange,
    // El silencio ya NO llega por props: vive en un store compartido y
    // persistido por empresa. Asi el Panel de Control y el gestor global (que
    // se montan por separado) ven el mismo estado, y navegar no lo pierde.
    onToggleGlobalMute,
    onToggleVehicleMute
}) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const { showSuccess, showError } = useNotification();
    const { currentCompany } = useAuth();
    const {
        globalMute,
        vehicleMutes,
        toggleVehicleMute: muteVehicle,
    } = useAlarmMute(currentCompany?.company_id || currentCompany?.id || null);

    // 🔄 WEBSOCKET: Usar hooks de WebSocket para tickets y alarmas en tiempo real
    const {
        tickets: activeTickets,
        openTicketCount,
        requiresAudio: wsRequiresAudio,
        lastTicket
    } = useTicketSubscription();

    const {
        alarms: realtimeAlarms,
        activeAlarmCount,
        lastAlarm
    } = useAlarmSubscription();

    // Local state
    const [showTicketDetails, setShowTicketDetails] = useState(false);
    // Firma del aviso descartado. Se compara contra el estado actual: mientras
    // coincida, el aviso queda oculto; si cambia el número de tickets o el estado
    // del sonido, vuelve a mostrarse porque ya es información nueva.
    const [dismissedSignature, setDismissedSignature] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // El audio ya no vive aqui: lo gestiona `alarmSoundService`, un singleton
    // de modulo. Antes cada montaje de este componente creaba su propio Audio
    // (App y Panel de Control lo montan) y al navegar el sonido se cortaba.

    // Check if should play alarm based on tickets
    const shouldPlayAlarmForTickets = useCallback((tickets) => {
        if (globalMute) {
            console.log(t('hardcoded.components_schematics_alarmmanager.global_mute_is_on_-'));
            return false;
        }

        // Check for open tickets from non-muted vehicles
        const openTicketsNotMuted = tickets.filter(ticket =>
            ticket.status === 'open' && !vehicleMutes[ticket.vehicle_id]
        );

        if (openTicketsNotMuted.length > 0) {
            console.log(`🚨 Found ${openTicketsNotMuted.length} open tickets from non-muted vehicles`);
            return true;
        }

        console.log(t('hardcoded.components_schematics_alarmmanager.all_open_tickets_are_from'));
        return false;
    }, [globalMute, vehicleMutes]);

    // Arrancar/parar es una sola llamada idempotente al servicio: da igual
    // cuantas alarmas haya, el loop es uno solo.
    const startAlarmSound = useCallback(() => { syncAlarmSound(true); }, []);
    const stopAlarmSound = useCallback(() => { syncAlarmSound(false); }, []);

    // Estado sonoro dirigido por WebSocket: se DECLARA si debe sonar y el
    // servicio decide (no reinicia si ya suena, no acumula instancias).
    useEffect(() => {
        const shouldPlay = shouldPlayAlarmForTickets(activeTickets);
        const soundState = syncAlarmSound(shouldPlay);
        setIsPlaying(soundState.isPlaying);

        if (onAlarmStateChange) {
            const openTicketsCount = activeTickets.filter(t => t.status === 'open').length;
            const acknowledgedTicketsCount = activeTickets.filter(t => t.status === 'acknowledged').length;

            onAlarmStateChange({
                totalTickets: activeTickets.length,
                openTickets: openTicketsCount,
                acknowledgedTickets: acknowledgedTicketsCount,
                unacknowledgedTickets: openTicketsCount,
                globalMute,
                mutedVehicles: Object.keys(vehicleMutes).filter(vid => vehicleMutes[vid]).length
            });
        }
    }, [activeTickets, globalMute, vehicleMutes, onAlarmStateChange, shouldPlayAlarmForTickets]);

    // 🔄 Helper functions para tickets (llamadas HTTP para acciones)
    const getActiveTicketsByVehicle = useCallback((vehicleId) => {
        return activeTickets.filter(ticket => ticket.vehicle_id === vehicleId);
    }, [activeTickets]);

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case 'open': return 'error';
            case 'acknowledged': return 'warning';
            case 'resolved': return 'success';
            default: return 'default';
        }
    }, []);

    const getStatusLabel = useCallback((status) => {
        switch (status) {
            case 'open': return t('alarmTickets.status.open');
            case 'acknowledged': return t('alarmTickets.status.acknowledged');
            case 'resolved': return t('alarmTickets.status.resolved');
            default: return status;
        }
    }, []);

    // Acknowledge tickets via API (el WebSocket recibirá la actualización)
    const acknowledgeTickets = useCallback(async (ticketIds) => {
        try {
            for (const ticketId of ticketIds) {
                await api.alarmTickets.acknowledge(ticketId);
            }
            console.log(t('hardcoded.components_schematics_alarmmanager.acknowledged_length_tickets', { length: ticketIds.length }));
            // WebSocket recibirá ticket_acknowledged automáticamente
        } catch (error) {
            console.error(t('hardcoded.components_schematics_alarmmanager.error_acknowledging_tickets'), error);
            showError(t('hardcoded.components_schematics_alarmmanager.error_al_confirmar_tickets'));
        }
    }, [showError]);

    const acknowledgeVehicleTickets = useCallback(async (vehicleId) => {
        const vehicleTickets = getActiveTicketsByVehicle(vehicleId);
        const openTicketIds = vehicleTickets
            .filter(ticket => ticket.status === 'open')
            .map(ticket => ticket.ticket_id);

        if (openTicketIds.length > 0) {
            await acknowledgeTickets(openTicketIds);
        }
    }, [getActiveTicketsByVehicle, acknowledgeTickets]);

    // Handle vehicle mute with ticket acknowledgment
    const handleVehicleMute = useCallback(async (vehicleId) => {
        // Silenciar es SOLO sonido: no reconoce, no marca como visto y no
        // resuelve. Antes esto llamaba a acknowledgeVehicleTickets y el usuario
        // perdia el estado real del ticket por bajar el volumen.
        muteVehicle(vehicleId);
        if (onToggleVehicleMute) onToggleVehicleMute(vehicleId);
    }, [muteVehicle, onToggleVehicleMute]);

    // Acknowledge all open tickets
    const acknowledgeAllTickets = useCallback(async () => {
        try {
            const openTicketIds = activeTickets
                .filter(ticket => ticket.status === 'open')
                .map(ticket => ticket.ticket_id);

            if (openTicketIds.length > 0) {
                await acknowledgeTickets(openTicketIds);
            }

            stopAlarmSound();
            console.log('✅ All tickets acknowledged');
        } catch (error) {
            console.error('Error acknowledging all tickets:', error);
            showError(t('hardcoded.components_schematics_alarmmanager.error_al_confirmar_tickets'));
        }
    }, [activeTickets, acknowledgeTickets, stopAlarmSound, showError]);

    // Prueba manual: suena unos segundos y ademas desbloquea el autoplay.
    const testAlarmSound = useCallback(async () => {
        await playTestSound(3000);
    }, []);

    const openTickets = activeTickets.filter(ticket => ticket.status === 'open');

    // Vehículos silenciados individualmente: es un estado distinto del silencio
    // global y el operador necesita distinguirlos ("no suena" no es lo mismo que
    // "no suena para este vehículo").
    const mutedVehicleCount = Object.keys(vehicleMutes || {})
        .filter((vehicleId) => vehicleMutes[vehicleId]).length;

    const alarmSoundLabel = globalMute
        ? 'Sonido silenciado'
        : mutedVehicleCount > 0
            ? `${mutedVehicleCount === 1 ? '1 vehículo silenciado' : `${mutedVehicleCount} vehículos silenciados`}`
            : isPlaying
                ? 'Sonando'
                : 'Toca para ver';

    // Lo que hace "nuevo" a un aviso ya descartado.
    const alarmSignature = `${openTickets.length}|${globalMute ? 'mute' : 'on'}|${mutedVehicleCount}`;
    const acknowledgedTicketsCount = activeTickets.filter(ticket => ticket.status === 'acknowledged').length;

    // Animation keyframes
    const animationKeyframes = `
        @keyframes alarm-flash {
            0% { background-color: rgba(244, 67, 54, 0.1); }
            50% { background-color: rgba(244, 67, 54, 0.2); }
            100% { background-color: rgba(244, 67, 54, 0.1); }
        }
        
        @keyframes pulse-critical {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
        }

        @keyframes sound-wave {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(1.4); opacity: 0; }
        }
    `;

    return (
        <>
            {/* Add keyframes to head */}
            <style dangerouslySetInnerHTML={{ __html: animationKeyframes }} />

            {/* Ticket Details Dialog */}
            <Dialog
                open={showTicketDetails}
                onClose={() => setShowTicketDetails(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TicketIcon color="error" />
                        <Typography variant="h6">
                            {i18n.t('hardcoded.components_schematics_alarmmanager.tickets_activos')}{activeTickets.length})
                        </Typography>
                        {isPlaying && !globalMute && (
                            <Chip
                                label={t('hardcoded.components_schematics_alarmmanager.sonando')}
                                size="small"
                                color="error"
                                variant="filled"
                            />
                        )}
                        {globalMute && (
                            <Chip
                                label={t('hardcoded.components_schematics_alarmmanager.silenciado_global')}
                                size="small"
                                color="warning"
                                variant="filled"
                            />
                        )}
                        {/* 🔄 WEBSOCKET: Indicador de conexión */}
                        <Chip
                            label={i18n.t('hardcoded.components_schematics_alarmmanager.live_indicator')}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ ml: 'auto' }}
                        />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {activeTickets.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                            <Typography variant="h6" color="success.main">
                                {t('hardcoded.components_schematics_alarmmanager.no_hay_tickets_activos')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('hardcoded.components_schematics_alarmmanager.todos_los_sistemas_funcionan_normalmente')}</Typography>
                        </Box>
                    ) : (
                        <List>
                            {activeTickets.map(ticket => {
                                const vehicle = vehicles.find(v => v.id === ticket.vehicle_id);
                                const vehicleName = vehicle?.display_name || vehicle?.alias || t('hardcoded.components_alarms_alarmlist.vehiculo_desconocido');

                                return (
                                    <ListItem
                                        key={ticket.ticket_id}
                                        sx={{
                                            border: 1,
                                            borderColor: getStatusColor(ticket.status) + '.main',
                                            borderRadius: 1,
                                            mb: 1,
                                            bgcolor: getStatusColor(ticket.status) + '.light'
                                        }}
                                    >
                                        <ListItemIcon>
                                            {ticket.status === 'open' ?
                                                <ErrorIcon color="error" /> :
                                                <WarningIcon color="warning" />
                                            }
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        {vehicleName} {t('hardcoded.components_schematics_alarmmanager.-_posicion')}{ticket.position}
                                                    </Typography>
                                                    {vehicleMutes[ticket.vehicle_id] && (
                                                        <Chip
                                                            label={t('hardcoded.components_schematics_alarmmanager.silenciado')}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {ticket.metric_type}: {ticket.current_value?.toFixed(1)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {i18n.t('hardcoded.components_admin_ordermanagement.estado')}{getStatusLabel(ticket.status)} •
                                                        {t('hardcoded.components_schematics_alarmmanager.id_prefix', { id: ticket.ticket_id.slice(-8) })}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {ticket.status === 'open' && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => acknowledgeTickets([ticket.ticket_id])}
                                                        title={t('hardcoded.components_schematics_alarmmanager.marcar_como_visto')}
                                                    >
                                                        <CheckIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={testAlarmSound}
                        color="info"
                        variant="outlined"
                        startIcon={<UnmuteIcon />}
                    >
                        {i18n.t('hardcoded.components_schematics_alarmmanager.probar_sonido')}</Button>
                    {openTickets.length > 0 && (
                        <Button
                            onClick={acknowledgeAllTickets}
                            color="primary"
                            variant="outlined"
                        >
                            {t('hardcoded.components_schematics_alarmmanager.marcar_todos_como_vistos')}</Button>
                    )}
                    <Button
                        onClick={() => setShowTicketDetails(false)}
                        variant="contained"
                    >
                        {t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Aviso flotante de tickets abiertos.
                Es descartable: tapaba prioridades, búsqueda y controles del panel,
                sobre todo en móvil. Al cerrarlo no reaparece por un simple
                re-render; solo vuelve si cambia algo que el operador debe saber
                (más/menos tickets, o el estado del sonido). */}
            {openTickets.length > 0 && dismissedSignature !== alarmSignature && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: { xs: 'auto', sm: 100 },
                        bottom: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 'auto' },
                        right: { xs: 12, sm: 20 },
                        left: { xs: 12, sm: 'auto' },
                        // Por debajo de drawers (1200) y modales (1300): antes estaba
                        // en 1300 y llegaba a taparlos, incluido el Asistente IA.
                        zIndex: 1100,
                        maxWidth: { xs: 'none', sm: 280 },
                        bgcolor: globalMute ? 'warning.main' : 'error.main',
                        color: 'white',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        animation: !globalMute && isPlaying ? 'pulse-critical 2s infinite' : 'none',
                        boxShadow: 3,
                    }}
                >
                    <TicketIcon fontSize="small" />
                    <Box
                        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => setShowTicketDetails(true)}
                    >
                        <Typography variant="body2" fontWeight={700} lineHeight={1.2} noWrap>
                            {openTickets.length === 1
                                ? '1 ticket abierto'
                                : `${openTickets.length} tickets abiertos`}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap component="div">
                            {alarmSoundLabel}
                        </Typography>
                    </Box>
                    <Tooltip title="Ocultar aviso">
                        <IconButton
                            size="small"
                            aria-label="Ocultar aviso de tickets"
                            onClick={() => setDismissedSignature(alarmSignature)}
                            sx={{ color: 'inherit', flexShrink: 0 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Audio status indicator for debugging */}
            {process.env.NODE_ENV === 'development' && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: 20,
                        zIndex: 1300,
                        bgcolor: 'info.main',
                        color: 'white',
                        borderRadius: 1,
                        p: 1,
                        fontSize: '12px'
                    }}
                >
                    🔌 WS |
                    Audio: {isPlaying ? '🔊' : '🔇'} |
                    Playing: {isPlaying ? '🔊' : '🔇'} {i18n.t('hardcoded.components_schematics_alarmmanager.tickets')}{openTickets.length} {i18n.t('hardcoded.components_schematics_alarmmanager.global')}{globalMute ? t('hardcoded.components_schematics_alarmmanager.muted') : t('hardcoded.components_schematics_alarmmanager.ok')} {i18n.t('hardcoded.components_schematics_alarmmanager.individual')}{Object.keys(vehicleMutes).filter(vid => vehicleMutes[vid]).length}
                </Box>
            )}
        </>
    );
};

export default AlarmManager;